import { useAsync } from "@react-hookz/web";
import { useCallback, useEffect, useState } from "react";
import { useLanes } from "store/Lanes.context";
import { TriggerMappingResponse } from "../../../../../backend/src/lanes/types";
import { getTriggerMapping, setTriggerMapping } from "../../../api/lanes";
import { TriggerEntry } from "./types";

type UpdatedTriggerMapping = TriggerEntry[];

export const useLaneTriggerMappingHook = () => {
  const [isTriggerUsed, setIsTriggerUsed] = useState<boolean>();

  const { selectedLaneId } = useLanes();
  const [updatedTriggerMapping, setUpdatedTriggerMapping] =
    useState<UpdatedTriggerMapping>();
  const [triggerMappingSelectedLane, setTriggerMappingSelectedLane] =
    useState<TriggerMappingResponse>();

  const [triggerMappingRequestState, { execute: doFetchTriggerMapping }] =
    useAsync(async (laneId: string) => {
      try {
        const response = await getTriggerMapping(laneId);
        if (response.status === 204) {
          setIsTriggerUsed(false);
        } else {
          setIsTriggerUsed(true);
        }
        return response.data;
      } catch (e) {
        setIsTriggerUsed(true);
        throw e;
      }
    });

  const fetchTriggerMappingSelectedLane = useCallback(async () => {
    if (selectedLaneId) {
      try {
        setTriggerMappingSelectedLane(
          await doFetchTriggerMapping(selectedLaneId)
        );
      } catch (e) {
        setTriggerMappingSelectedLane(undefined);
      }
    } else {
      setTriggerMappingSelectedLane(undefined);
    }
  }, [doFetchTriggerMapping, selectedLaneId]);

  useEffect(() => {
    if (!selectedLaneId) {
      return setTriggerMappingSelectedLane(undefined);
    }
    fetchTriggerMappingSelectedLane();
  }, [fetchTriggerMappingSelectedLane, selectedLaneId]);

  const executeTriggerUpdate = useCallback(
    async (laneId: string, mapping: UpdatedTriggerMapping) => {
      const created = await setTriggerMapping(
        laneId,
        mapping.map((m) => {
          const { id, laneId, ...rest } = m;
          return rest;
        })
      );
      await fetchTriggerMappingSelectedLane();
      return created;
    },
    [fetchTriggerMappingSelectedLane]
  );

  return {
    isTriggerUsed,
    isTriggerMappingLoading: triggerMappingRequestState.status === "loading",
    triggerMappingSelectedLane,
    updatedTriggerMapping,
    setUpdatedTriggerMapping,
    executeTriggerUpdate,
  };
};

export type TriggerMappingHook = ReturnType<typeof useLaneTriggerMappingHook>;
